/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Per-molecular-species force-field parameter packs for the crystal
 * MD engine. Each pack covers:
 *   - per-element LJ parameters (sigma, epsilon)
 *   - per-element point charges
 *   - per-bond harmonic strength + equilibrium length
 *   - per-angle harmonic strength + equilibrium angle
 *
 * Detection is topology-driven: count basis atoms by element and look
 * for the canonical signatures (H₂O = 2H:1O, NH₃ = 3H:1N, …). The
 * detector falls through to `null` for crystals it doesn't recognize;
 * the supercell builder then uses generic Element-radius defaults
 * (which give qualitative but not quantitative MD behavior).
 *
 * v1: water (TIP3P) only. Methane, ammonia, urea, benzene packs are
 * straightforward follow-ups — same shape, different numbers.
 */
import { BasisAtom } from '../../types.ts';
import { Atom } from '../Atom.ts';
import { VirtualSite } from './VirtualSite.ts';

/** Recognized molecular species. Extend as new packs land. */
export type MolecularSpecies =
  | 'water'
  | 'co2'
  | 'n2'
  | 'methane'
  | 'ammonia'
  | 'phosphine'
  | 'arsine'
  | 'cbr4'
  | 'cf4'
  | 'ccl4'
  | 'silane'
  | 'germane'
  | 'cs2'
  | 'n2o'
  | 'h2'
  | 'co'
  | 'acetylene'
  | 'hcn'
  | 'cyanogen'
  | 'sf6'
  | 'p4';

/**
 * TIP3P parameters in our internal unit system (Å, eV, e).
 *
 * Original TIP3P values from Jorgensen et al. 1983:
 *   σ_O = 3.15066 Å, ε_O = 0.1521 kcal/mol = 0.006596 eV
 *   σ_H = 0,        ε_H = 0
 *   q_O = -0.834 e, q_H = +0.417 e
 *
 * We give H a tiny nonzero σ/ε to avoid singularities in the LJ
 * combining rule (Lorentz-Berthelot would otherwise produce
 * sigmaMean ≈ σ_O/2 for any O–H pair, and the σ⁶/r⁶ term blows up
 * when r is small at the bonded geometry).
 *
 * Original bonded params from CHARMM-flavored flexible TIP3P:
 *   k_OH = 553 kcal/(mol·Å²) ≈ 24 eV/Å², r_OH = 0.9572 Å
 *   k_HOH = 55 kcal/(mol·rad²) ≈ 2.39 eV/rad², θ_HOH = 104.52°
 */
export const TIP3P = {
  O: {
    sigma: 3.151, // Å
    epsilon: 0.006596, // eV
    charge: -0.834, // e
  },
  H: {
    // Small but nonzero — see comment above. ε is two decades below
    // the O well depth so cross-pair O–H interactions are dominated
    // by Coulomb (the correct physics), not LJ.
    sigma: 0.4,
    epsilon: 0.0001,
    charge: 0.417,
  },
  /** O–H bond harmonic strength (eV/Å²). */
  bondStrength: 24,
  /** O–H bond equilibrium length (Å). */
  bondLength: 0.9572,
  /** H–O–H angle harmonic strength (eV/rad²). */
  angleStrength: 2.39,
  /** H–O–H equilibrium angle (radians). */
  angleEquilibrium: (104.52 * Math.PI) / 180,
} as const;

/**
 * Topology-based species detector. Counts the basis atoms by element
 * and returns the matching species, or null if none matches.
 *
 * Ice (Ih) basis is multiple water molecules in the unit cell (8 H₂O
 * for proton-disordered Ih), so the 2H:1O ratio holds regardless of
 * how many copies are packed in.
 */
export const detectMolecularSpecies = (basis: BasisAtom[]): MolecularSpecies | null => {
  if (basis.length === 0) return null;
  const counts = new Map<string, number>();
  for (const ba of basis) {
    counts.set(ba.element, (counts.get(ba.element) ?? 0) + 1);
  }
  // Reject anything that isn't pure {O, H} for water detection.
  const elements = Array.from(counts.keys()).sort();
  if (elements.length === 2 && elements[0] === 'H' && elements[1] === 'O') {
    const h = counts.get('H') ?? 0;
    const o = counts.get('O') ?? 0;
    // Allow small rounding (ice Ih basis has exactly 2:1; other ice
    // phases may have proton disorder but the ratio holds).
    if (o > 0 && h === 2 * o) return 'water';
  }
  // CO₂ detection: pure {C, O} with 1:2 ratio. Dry ice has 4 CO₂ per
  // cell → 4 C + 8 O = 12 atoms. Any other crystal with C and O in the
  // 1:2 stoichiometry would also resolve here — none in our preset list.
  if (elements.length === 2 && elements[0] === 'C' && elements[1] === 'O') {
    const c = counts.get('C') ?? 0;
    const o = counts.get('O') ?? 0;
    if (c > 0 && o === 2 * c) return 'co2';
  }
  // N₂ detection: pure {N} with an even atom count (every N pairs with
  // another N as a diatomic). α-N₂ has 4 N₂ per cell = 8 N atoms.
  // Single-element {N} with odd count would be ambiguous (atomic N) and
  // is not a recognized solid; we'd fall through to null there.
  if (elements.length === 1 && elements[0] === 'N') {
    const n = counts.get('N') ?? 0;
    if (n > 0 && n % 2 === 0) return 'n2';
  }
  // Methane detection: pure {C, H} with 4:1 H:C ratio. Solid CH₄
  // Phase I has 4 CH₄ per cell = 4 C + 16 H = 20 atoms. The 4:1
  // signature distinguishes CH₄ from benzene (1:1), naphthalene
  // (4:5), or any future organic crystal with a different topology.
  if (elements.length === 2 && elements[0] === 'C' && elements[1] === 'H') {
    const c = counts.get('C') ?? 0;
    const h = counts.get('H') ?? 0;
    if (c > 0 && h === 4 * c) return 'methane';
  }
  // Ammonia detection: pure {H, N} with 3:1 H:N ratio. P2₁3 solid
  // NH₃ has 4 NH₃ per cell = 4 N + 12 H = 16 atoms. The 3:1 signature
  // distinguishes NH₃ from hydrazine N₂H₄ (4:2 → 2:1) or HCN.
  if (elements.length === 2 && elements[0] === 'H' && elements[1] === 'N') {
    const h = counts.get('H') ?? 0;
    const n = counts.get('N') ?? 0;
    if (n > 0 && h === 3 * n) return 'ammonia';
  }
  // Phosphine detection: pure {H, P} with 3:1 H:P ratio. Same shape
  // signature as NH₃ but with phosphorus — pyramidal molecule with
  // much narrower H-P-H angle (~93.5°) and ~3× weaker dipole.
  if (elements.length === 2 && elements[0] === 'H' && elements[1] === 'P') {
    const h = counts.get('H') ?? 0;
    const p = counts.get('P') ?? 0;
    if (p > 0 && h === 3 * p) return 'phosphine';
  }
  // Arsine detection: pure {As, H} with 3:1 H:As ratio. Pyramidal
  // group-15 hydride one row heavier than PH₃ — narrower H-As-H
  // angle (~91.8°, almost pure p-orbital bonding) and smaller dipole.
  if (elements.length === 2 && elements[0] === 'As' && elements[1] === 'H') {
    const as = counts.get('As') ?? 0;
    const h = counts.get('H') ?? 0;
    if (as > 0 && h === 3 * as) return 'arsine';
  }
  // CBr₄ (carbon tetrabromide) detection: pure {Br, C} with 4:1 Br:C
  // ratio. Same tetrahedral shape as CH₄ but with bromine — a much
  // more polarizable atom (large London dispersion) and a "plastic
  // crystal" (rotationally disordered FCC) in real life.
  if (elements.length === 2 && elements[0] === 'Br' && elements[1] === 'C') {
    const br = counts.get('Br') ?? 0;
    const c = counts.get('C') ?? 0;
    if (c > 0 && br === 4 * c) return 'cbr4';
  }
  // CF₄ detection: pure {C, F} with 4:1 F:C ratio. Tetrahedral 5-atom
  // CH₄ analog with the most electronegative element — large partial
  // charges, but molecular dipole zero by symmetry. Real solid CF₄ at
  // low T is orientationally ordered (Phase II, monoclinic).
  if (elements.length === 2 && elements[0] === 'C' && elements[1] === 'F') {
    const c = counts.get('C') ?? 0;
    const f = counts.get('F') ?? 0;
    if (c > 0 && f === 4 * c) return 'cf4';
  }
  // CCl₄ detection: pure {C, Cl} with 4:1 Cl:C ratio. Tetrahedral
  // 5-atom CBr₄ analog — same geometric clash issue at the experimental
  // FCC lattice with identical-orientation preset packing.
  if (elements.length === 2 && elements[0] === 'C' && elements[1] === 'Cl') {
    const c = counts.get('C') ?? 0;
    const cl = counts.get('Cl') ?? 0;
    if (c > 0 && cl === 4 * c) return 'ccl4';
  }
  // Silane SiH₄ detection: pure {H, Si} with 4:1 H:Si ratio. Same
  // tetrahedral shape as CH₄ with silicon at the center (longer Si-H
  // bond, larger Si vdW). Solid SiH₄ is a cubic plastic crystal.
  if (elements.length === 2 && elements[0] === 'H' && elements[1] === 'Si') {
    const h = counts.get('H') ?? 0;
    const si = counts.get('Si') ?? 0;
    if (si > 0 && h === 4 * si) return 'silane';
  }
  // Germane GeH₄ detection: pure {Ge, H} with 4:1 H:Ge ratio. Same
  // tetrahedral shape as CH₄/SiH₄ with germanium at the center.
  if (elements.length === 2 && elements[0] === 'Ge' && elements[1] === 'H') {
    const ge = counts.get('Ge') ?? 0;
    const h = counts.get('H') ?? 0;
    if (ge > 0 && h === 4 * ge) return 'germane';
  }
  // Carbon disulfide CS₂ detection: pure {C, S} with 1:2 C:S ratio.
  // Same linear-triatomic shape as CO₂ (sulfur in place of oxygen).
  if (elements.length === 2 && elements[0] === 'C' && elements[1] === 'S') {
    const c = counts.get('C') ?? 0;
    const s = counts.get('S') ?? 0;
    if (c > 0 && s === 2 * c) return 'cs2';
  }
  // Nitrous oxide N₂O detection: pure {N, O} with 2:1 N:O ratio.
  // Asymmetric linear N≡N-O — same overall shape as CO₂ but the
  // central atom is N (not C) and one terminal is N, the other O.
  if (elements.length === 2 && elements[0] === 'N' && elements[1] === 'O') {
    const n = counts.get('N') ?? 0;
    const o = counts.get('O') ?? 0;
    if (o > 0 && n === 2 * o) return 'n2o';
  }
  // Solid hydrogen H₂ detection: pure {H} with an even atom count.
  // Same shape as N₂ but lighter and weaker-bound; quantum effects
  // dominate at very low T but classical LJ is fine for pedagogy.
  if (elements.length === 1 && elements[0] === 'H') {
    const h = counts.get('H') ?? 0;
    if (h > 0 && h % 2 === 0) return 'h2';
  }
  // Solid CO detection: pure {C, O} with 1:1 ratio. Diatomic with a
  // very small dipole — distinguishes from CO₂ (1:2) by ratio.
  if (elements.length === 2 && elements[0] === 'C' && elements[1] === 'O') {
    const c = counts.get('C') ?? 0;
    const o = counts.get('O') ?? 0;
    if (c > 0 && o === c) return 'co';
  }
  // Acetylene C₂H₂ detection: pure {C, H} with 1:1 ratio. Linear
  // 4-atom H-C≡C-H — distinguishes from methane (4:1 H:C) and the
  // hypothetical benzene/naphthalene by ratio. Solid C₂H₂ at low T
  // is orthorhombic; the SOLID_ACETYLENE preset is a Pa-3-like
  // pedagogical representation with 4 linear molecules.
  if (elements.length === 2 && elements[0] === 'C' && elements[1] === 'H') {
    const c = counts.get('C') ?? 0;
    const h = counts.get('H') ?? 0;
    if (c > 0 && h === c) return 'acetylene';
  }
  // HCN detection: ternary {C, H, N} with 1:1:1 ratio. Linear
  // asymmetric H-C≡N — the strongest dipole of any small linear
  // molecule (μ ≈ 2.98 D), real solid HCN forms H-bonded chains.
  if (elements.length === 3 && elements[0] === 'C' && elements[1] === 'H' && elements[2] === 'N') {
    const c = counts.get('C') ?? 0;
    const h = counts.get('H') ?? 0;
    const n = counts.get('N') ?? 0;
    if (c > 0 && h === c && n === c) return 'hcn';
  }
  // Cyanogen C₂N₂ detection: pure {C, N} with 1:1 ratio. Linear
  // symmetric N≡C-C≡N — centrosymmetric, zero dipole, large axial
  // quadrupole. Distinguishes from CN polymorphs by exactly 1:1
  // stoichiometry; an isolated CN radical has odd electron count
  // and is not a stable molecular crystal at this level.
  if (elements.length === 2 && elements[0] === 'C' && elements[1] === 'N') {
    const c = counts.get('C') ?? 0;
    const n = counts.get('N') ?? 0;
    if (c > 0 && n === c) return 'cyanogen';
  }
  // SF₆ detection: pure {F, S} with 6:1 F:S ratio. Octahedral 7-atom
  // molecule — first non-tetrahedral / non-pyramidal / non-linear
  // shape in the molecular pack. Highly symmetric (zero dipole, zero
  // quadrupole), so even the very polar S-F bonds (q_S = +1.20)
  // contribute almost nothing to lattice cohesion by themselves.
  if (elements.length === 2 && elements[0] === 'F' && elements[1] === 'S') {
    const f = counts.get('F') ?? 0;
    const s = counts.get('S') ?? 0;
    if (s > 0 && f === 6 * s) return 'sf6';
  }
  // White phosphorus α-P₄ detection: pure {P} with atom count
  // divisible by 4 (each tetrahedron contributes 4 P). Distinguishes
  // from any hypothetical mono-P or P₂ phase by the multiple-of-4
  // rule; a single isolated P atom is not a stable molecular solid.
  if (elements.length === 1 && elements[0] === 'P') {
    const p = counts.get('P') ?? 0;
    if (p > 0 && p % 4 === 0) return 'p4';
  }
  return null;
};

/**
 * Apply TIP3P per-atom parameters in place. Called by CrystalViewer
 * after the supercell is expanded and before the MD engine is built,
 * so the integrator sees water atoms with the right σ/ε/charge from
 * frame zero.
 */
export const applyTip3PToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'O') {
      a.sigma = TIP3P.O.sigma;
      a.epsilon = TIP3P.O.epsilon;
      a.charge = TIP3P.O.charge;
    } else if (a.elementSymbol === 'H') {
      a.sigma = TIP3P.H.sigma;
      a.epsilon = TIP3P.H.epsilon;
      a.charge = TIP3P.H.charge;
    }
  }
};

/**
 * TIP4P/Ice parameters (Abascal, Sanz, García Fernández, Vega
 * J. Chem. Phys. 122, 234511 (2005)). Same molecular geometry as
 * TIP3P but the negative charge sits on an M-site offset from the
 * oxygen toward the H-H bisector — this is what gives TIP4P/Ice
 * its ~272 K melting point (vs TIP3P's ~146 K), the closest of the
 * common rigid water models to real ice's 273.15 K.
 *
 *   σ_O = 3.1668 Å, ε_O = 0.21084 kJ/mol = 0.002187 eV
 *   q_O = 0  (oxygen has no charge — all of it is on M)
 *   q_M = -1.1794 e
 *   q_H = +0.5897 e
 *   d_OH = 0.9572 Å, θ_HOH = 104.52°, d_OM = 0.1577 Å
 *
 * The linear-M-site weights are derived from the rigid geometry:
 *   α = d_OM / (d_OH · cos(θ_HOH/2))
 *      = 0.1577 / (0.9572 · cos(52.26°)) ≈ 0.269
 * So M = (1 − α)·O + (α/2)·H₁ + (α/2)·H₂.
 */
export const TIP4P_ICE = {
  O: {
    sigma: 3.1668,
    epsilon: 0.002187,
    charge: 0,
  },
  H: {
    // Tiny LJ on H — same combining-rule safety trick as TIP3P.
    sigma: 0.4,
    epsilon: 0.0001,
    charge: 0.5897,
  },
  M: {
    charge: -1.1794,
  },
  /** O-H bond and H-O-H angle (matching TIP3P flexible variants for the harmonic constants). */
  bondStrength: 24,
  bondLength: 0.9572,
  angleStrength: 2.39,
  angleEquilibrium: (104.52 * Math.PI) / 180,
  /** d_OM, the offset of the M-site from the oxygen along the bisector. */
  dOM: 0.1577,
  /** Linear-combination weight on O for M = w_O·O + w_H·(H₁+H₂). */
  wO: 0.731,
  /** Linear-combination weight on each H for M. */
  wH: 0.1345,
} as const;

/**
 * Apply TIP4P/Ice per-atom parameters in place. Oxygens get zero
 * charge (the charge lives on the M-site, built separately by
 * `buildTip4PIceVirtualSites`). Hydrogens get the standard H charge.
 */
export const applyTip4PIceToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'O') {
      a.sigma = TIP4P_ICE.O.sigma;
      a.epsilon = TIP4P_ICE.O.epsilon;
      a.charge = TIP4P_ICE.O.charge;
    } else if (a.elementSymbol === 'H') {
      a.sigma = TIP4P_ICE.H.sigma;
      a.epsilon = TIP4P_ICE.H.epsilon;
      a.charge = TIP4P_ICE.H.charge;
    }
  }
};

/**
 * Build the per-molecule M-site virtual sites. Walks the radial bond
 * adjacency to identify H₂O clusters (one O with exactly two H
 * neighbors) and creates a VirtualSite for each, anchored on the
 * linear combination of (O, H₁, H₂) that puts the M-site at d_OM
 * along the bisector when the molecule is at equilibrium geometry.
 *
 * Returns the freshly-built virtual sites; caller mutates them into
 * `crystalDynamics.virtualSites` (and the pair kernel's mirror).
 */
export const buildTip4PIceVirtualSites = (
  atoms: Atom[],
  radialBonds: { atom1: Atom; atom2: Atom }[],
): VirtualSite[] => {
  // Build per-O neighbor list of H atoms via the bond graph.
  const idx = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) idx.set(atoms[i], i);
  const hNeighborsOfO = new Map<number, number[]>();
  for (const rb of radialBonds) {
    const i = idx.get(rb.atom1);
    const j = idx.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    const ei = atoms[i].elementSymbol;
    const ej = atoms[j].elementSymbol;
    if (ei === 'O' && ej === 'H') {
      if (!hNeighborsOfO.has(i)) hNeighborsOfO.set(i, []);
      hNeighborsOfO.get(i)!.push(j);
    } else if (ei === 'H' && ej === 'O') {
      if (!hNeighborsOfO.has(j)) hNeighborsOfO.set(j, []);
      hNeighborsOfO.get(j)!.push(i);
    }
  }
  const sites: VirtualSite[] = [];
  for (const [oIdx, hIdxs] of hNeighborsOfO) {
    // Only build a site for a well-formed H₂O. If the auto-bond
    // detector picked up an OH⁻ (1 H) or H₃O⁺ (3 H) we skip rather
    // than synthesize a junk M-site.
    if (hIdxs.length !== 2) continue;
    const O = atoms[oIdx];
    const H1 = atoms[hIdxs[0]];
    const H2 = atoms[hIdxs[1]];
    sites.push(new VirtualSite([O, H1, H2], [TIP4P_ICE.wO, TIP4P_ICE.wH, TIP4P_ICE.wH], TIP4P_ICE.M.charge));
  }
  return sites;
};

/**
 * EPM2 parameters for CO₂ (Harris & Yung, J. Phys. Chem. 99, 12021
 * (1995)). Rigid linear 3-site model with point charges that capture
 * CO₂'s strong axial quadrupole — the dominant intermolecular
 * interaction in dry ice. Standard for CO₂ simulations.
 *
 *   σ_C = 2.757 Å, ε_C = 28.129 K   = 0.002423 eV
 *   σ_O = 3.033 Å, ε_O = 80.507 K   = 0.006937 eV
 *   q_C = +0.6512 e, q_O = −0.3256 e   (molecule neutral)
 *   r_CO = 1.149 Å (rigid), θ_OCO = 180° (rigid)
 *
 * The molecule is held rigid by 3 distance constraints (C-O₁, C-O₂,
 * O₁-O₂) — same SHAKE/RATTLE mechanism as rigid water, but the third
 * constraint pins the O-C-O angle at 180° rather than 104.52°.
 *
 * Sublimation enthalpy of α-CO₂ at 0 K: ~0.276 eV/molecule = ~0.092
 * eV/atom — the cohesion target. About 80% of this is electrostatic
 * (quadrupole-quadrupole); LJ alone gives ~0.02 eV/atom, way short.
 * Adding partial charges is what makes CO₂ behave like real dry ice.
 */
const KB_EV_CO2 = 8.617333e-5; // eV/K — local copy to avoid cross-file import
export const EPM2_CO2 = {
  C: {
    sigma: 2.757,
    epsilon: 28.129 * KB_EV_CO2,
    charge: 0.6512,
  },
  O: {
    sigma: 3.033,
    epsilon: 80.507 * KB_EV_CO2,
    charge: -0.3256,
  },
  /** Rigid C-O bond length, Å. */
  bondLength: 1.149,
  /** Rigid linear O-C-O angle. The O-O distance constraint pins it. */
  oOoDistance: 2.298,
} as const;

/**
 * Apply EPM2 per-atom parameters in place. Called by the CO₂ force
 * field after the supercell is expanded and before the MD engine is
 * built, so the integrator sees CO₂ atoms with the right σ/ε/charge
 * from frame zero. Sets ONLY C and O — other elements (guest dopants)
 * are left alone.
 */
export const applyEpm2CO2ToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'C') {
      a.sigma = EPM2_CO2.C.sigma;
      a.epsilon = EPM2_CO2.C.epsilon;
      a.charge = EPM2_CO2.C.charge;
    } else if (a.elementSymbol === 'O') {
      a.sigma = EPM2_CO2.O.sigma;
      a.epsilon = EPM2_CO2.O.epsilon;
      a.charge = EPM2_CO2.O.charge;
    }
  }
};

/**
 * Etters N₂ parameters (R.D. Etters, J. Chem. Phys. 84, 7050 (1986),
 * simplest two-site rigid form). Pure LJ on each N atom + rigid bond:
 *
 *   σ_N = 3.31 Å, ε_N = 36 K ≈ 0.00310 eV
 *   q_N = 0 (no point charges — the simplest Etters form neglects the
 *           quadrupole; richer Etters/TraPPE-N₂ variants add a virtual
 *           central site to capture the quadrupole, not done here)
 *   r_NN = 1.098 Å (gas-phase N-N triple bond, rigid via SHAKE)
 *
 * α-N₂ sublimation enthalpy at 0 K: ~0.069 eV/molecule = ~0.034
 * eV/atom — a weak vdW solid (N₂ stable as a crystal only below
 * ~63 K). Two-site LJ-only N₂ gets close to this magnitude;
 * quadrupolar corrections (~25% of the cohesion) are absent in the
 * simplest form, so our number will be a few % low.
 *
 * ε RESCALED for cohesion (measured 2026-10-09): at the literature
 * ε = 36 K this engine's α-N₂ lattice bottoms out at −42 meV/molecule
 * (a = 5.65 Å, right where experiment puts it) — only 61 % of the
 * 69 meV above, the missing quadrupole plus the LJ kernel's
 * shifted-force truncation — and a free-surface slab melts at
 * 20–25 K instead of 63 K. ε × 69/42.4 restores the measured
 * cohesion (the same cohesion-matching used for the HCP metals in
 * eamParameters.ts); the lattice constant is untouched. Still a
 * quadrupole-free model: the solid is a plastic (rotator) crystal at
 * every temperature, and the melting point stays under experiment.
 */
export const ETTERS_N2 = {
  N: {
    sigma: 3.31,
    epsilon: 36 * (69 / 42.4) * KB_EV_CO2, // ≈ 0.00505 eV (58.6 K; 36 K as published)
    charge: 0,
  },
  /** Rigid N-N bond (Å), the gas-phase triple-bond length. */
  bondLength: 1.098,
} as const;

/**
 * Apply Etters N₂ per-atom parameters in place. Sets ONLY N atoms;
 * other elements (guest dopants) are left alone.
 */
export const applyEttersN2ToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'N') {
      a.sigma = ETTERS_N2.N.sigma;
      a.epsilon = ETTERS_N2.N.epsilon;
      a.charge = ETTERS_N2.N.charge;
    }
  }
};

/**
 * OPLS-AA parameters for methane (CH₄) — Jorgensen, Maxwell, Tirado-
 * Rives, J. Am. Chem. Soc. 118, 11225 (1996). All-atom rigid
 * tetrahedron with small partial charges that capture the C-H bond
 * polarity (CH₄'s molecular dipole is zero by symmetry, but the
 * partial charges still matter for intermolecular C↔H interactions).
 *
 *   σ_C = 3.5 Å,  ε_C = 0.276 kJ/mol = 2.86 meV
 *   σ_H = 2.5 Å,  ε_H = 0.125 kJ/mol = 1.30 meV
 *   q_C = −0.24 e, q_H = +0.06 e   (molecule neutral)
 *   r_CH = 1.087 Å (rigid), θ_HCH = 109.47° (tetrahedral, rigid)
 *
 * The rigid tetrahedron needs 10 distance constraints per CH₄:
 * 4 C-H bonds plus 6 H-H 1-3 distances. SHAKE/RATTLE handles the
 * redundant (only 6 are independent for 12 DOF − 6 trans+rot = 6
 * internal DOF, but the standard convention is all-pairs).
 *
 * Solid CH₄ Phase I sublimation enthalpy at 0 K: ~0.099 eV/molecule
 * (~0.020 eV/atom for 5-atom CH₄). OPLS-AA usually gets within ~10%.
 */
export const OPLS_CH4 = {
  C: {
    sigma: 3.5,
    epsilon: 0.276 / 96.485, // kJ/mol → eV; ≈ 0.00286
    charge: -0.24,
  },
  H: {
    sigma: 2.5,
    epsilon: 0.125 / 96.485, // ≈ 0.001296
    charge: 0.06,
  },
  /** Rigid C-H bond, Å. */
  bondLength: 1.087,
  /** H-C-H equilibrium angle (radians), tetrahedral. */
  angleEquilibrium: Math.acos(-1 / 3),
} as const;

/**
 * Apply OPLS-AA methane per-atom parameters in place. Sets ONLY C
 * and H — guest dopants of other elements are left alone.
 */
export const applyOplsCH4ToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'C') {
      a.sigma = OPLS_CH4.C.sigma;
      a.epsilon = OPLS_CH4.C.epsilon;
      a.charge = OPLS_CH4.C.charge;
    } else if (a.elementSymbol === 'H') {
      a.sigma = OPLS_CH4.H.sigma;
      a.epsilon = OPLS_CH4.H.epsilon;
      a.charge = OPLS_CH4.H.charge;
    }
  }
};

/**
 * OPLS-AA ammonia parameters (Jorgensen, Briggs; modernised in OPLS-AA
 * 1996). Rigid pyramidal NH₃ with large partial charges that capture
 * NH₃'s strong molecular dipole (μ ≈ 1.47 D — the source of nearly
 * all of solid NH₃'s cohesion, which is much stronger than CH₄'s).
 *
 *   σ_N = 3.42 Å, ε_N = 0.711 kJ/mol = 7.37 meV
 *   σ_H = 0 (safety floor 0.4 to avoid LJ-mix singularity, like TIP3P H)
 *   q_N = −1.02 e, q_H = +0.34 e   (molecule neutral; dipole ≈ 1.4 D)
 *   r_NH = 1.012 Å (rigid), θ_HNH = 106.7° (rigid pyramidal)
 *
 * The rigid pyramid needs 6 distance constraints per NH₃: 3 N-H bonds
 * plus 3 H-H 1-3 distances — exactly the 4·3 − 6 = 6 internal DOF.
 *
 * Solid NH₃ sublimation enthalpy at 0 K: ~0.31 eV/molecule (~0.077
 * eV/atom for 4-atom NH₃). The hydrogen bonding (linear N-H···N
 * across molecules) provides most of the cohesion.
 */
export const OPLS_NH3 = {
  N: {
    sigma: 3.42,
    epsilon: 0.711 / 96.485, // kJ/mol → eV; ≈ 0.00737
    charge: -1.02,
  },
  H: {
    // Like TIP3P/TIP4P H: tiny LJ to avoid Lorentz-Berthelot singularity
    // (the σ_OH cross-pair would be near 0 for σ_H = 0, blowing up the
    // r⁻⁶ term at the bonded geometry). ε three decades below N's well
    // depth so the cross-pair is dominated by Coulomb (the real physics).
    sigma: 0.4,
    epsilon: 0.0001,
    charge: 0.34,
  },
  /** Rigid N-H bond, Å. */
  bondLength: 1.012,
  /** H-N-H equilibrium angle (radians), pyramidal (less than tetrahedral due to lone pair). */
  angleEquilibrium: (106.7 * Math.PI) / 180,
} as const;

/**
 * Apply OPLS-AA ammonia per-atom parameters in place. Sets ONLY N
 * and H — guest dopants are left alone.
 */
export const applyOplsNH3ToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'N') {
      a.sigma = OPLS_NH3.N.sigma;
      a.epsilon = OPLS_NH3.N.epsilon;
      a.charge = OPLS_NH3.N.charge;
    } else if (a.elementSymbol === 'H') {
      a.sigma = OPLS_NH3.H.sigma;
      a.epsilon = OPLS_NH3.H.epsilon;
      a.charge = OPLS_NH3.H.charge;
    }
  }
};

/**
 * UFF-style phosphine (PH₃) parameters. PH₃ is ammonia's heavier
 * group-15 cousin but is MUCH less polar (μ ≈ 0.58 D vs NH₃'s 1.47
 * D) and forms weak-to-no hydrogen bonds — phosphorus is too far
 * from H in electronegativity. The H-P-H angle is also much narrower
 * (~93.5°, vs NH₃'s 106.7°) because phosphorus uses mostly pure
 * p-orbitals for bonding with little s-p hybridization.
 *
 *   σ_P = 3.695 Å, ε_P = 0.305 kcal/mol = 13.2 meV (UFF, 153 K)
 *   σ_H = 0.4 (safety, like NH₃), ε_H = 0.0001
 *   q_P = −0.36 e, q_H = +0.12 e   (AM1-BCC-style, ~3× smaller than NH₃)
 *   r_PH = 1.42 Å (rigid), θ_HPH = 93.5° (rigid pyramidal)
 *
 * Solid PH₃ sublimation enthalpy at 0 K: ~0.19 eV/molecule (~30 % of
 * NH₃'s 0.31). No proper H-bond network — cohesion is dominated by
 * London dispersion plus weak dipole-dipole.
 */
export const UFF_PH3 = {
  P: {
    sigma: 3.695,
    epsilon: 0.305 * 0.04336, // kcal/mol → eV; ≈ 0.01323
    charge: -0.36,
  },
  H: {
    // Same tiny-LJ safety H as the other H-bearing molecular FFs.
    sigma: 0.4,
    epsilon: 0.0001,
    charge: 0.12,
  },
  /** Rigid P-H bond, Å. */
  bondLength: 1.42,
  /** H-P-H equilibrium angle (radians), narrower than tetrahedral. */
  angleEquilibrium: (93.5 * Math.PI) / 180,
} as const;

/**
 * Apply UFF phosphine per-atom parameters in place. Sets ONLY P and H
 * — guest dopants are left alone.
 */
export const applyUffPH3ToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'P') {
      a.sigma = UFF_PH3.P.sigma;
      a.epsilon = UFF_PH3.P.epsilon;
      a.charge = UFF_PH3.P.charge;
    } else if (a.elementSymbol === 'H') {
      a.sigma = UFF_PH3.H.sigma;
      a.epsilon = UFF_PH3.H.epsilon;
      a.charge = UFF_PH3.H.charge;
    }
  }
};

/**
 * UFF-style arsine (AsH₃) parameters. Pyramidal molecule one row
 * heavier than PH₃. As is even less electronegative than P (2.18 vs
 * 2.19), so the As-H bond polarity is similarly tiny in magnitude
 * but the same sign: H slightly more electronegative, so q_As > 0 in
 * Pauling-electronegativity terms — but the EMPIRICAL dipole points
 * the OTHER way (lone pair contribution dominates), so we follow the
 * NH₃/PH₃ convention with q_As < 0 (As on the negative end). The
 * H-As-H angle is ~91.8° — almost a pure p-orbital bond angle, even
 * narrower than PH₃'s 93.5°. The molecular dipole is small (μ ≈
 * 0.20 D, ~⅓ of PH₃'s, ~1/7 of NH₃'s) — solid AsH₃ has effectively
 * no hydrogen bonding.
 *
 *   σ_As = 3.770 Å, ε_As = 0.309 kcal/mol = 13.4 meV (UFF)
 *   σ_H = 0.4 (safety floor — like TIP3P / NH₃ / PH₃ H)
 *   q_As = −0.15 e, q_H = +0.05 e   (~⅓ of PH₃'s charge magnitudes,
 *           matching the ~⅓ smaller AsH₃ dipole)
 *   r_AsH = 1.519 Å (rigid, gas-phase), θ_HAsH = 91.8° (rigid)
 *
 * Solid AsH₃ sublimation enthalpy at 0 K: ~0.20 eV/molecule. Cohesion
 * dominated by As-As London dispersion (As is large and polarizable).
 */
export const UFF_ASH3 = {
  As: {
    sigma: 3.77,
    epsilon: 0.309 * 0.04336, // kcal/mol → eV; ≈ 0.01340
    charge: -0.15,
  },
  H: {
    sigma: 0.4,
    epsilon: 0.0001,
    charge: 0.05,
  },
  /** Rigid As-H bond, Å. */
  bondLength: 1.519,
  /** H-As-H equilibrium angle (radians), nearly p-orbital. */
  angleEquilibrium: (91.8 * Math.PI) / 180,
} as const;

export const applyUffAsH3ToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'As') {
      a.sigma = UFF_ASH3.As.sigma;
      a.epsilon = UFF_ASH3.As.epsilon;
      a.charge = UFF_ASH3.As.charge;
    } else if (a.elementSymbol === 'H') {
      a.sigma = UFF_ASH3.H.sigma;
      a.epsilon = UFF_ASH3.H.epsilon;
      a.charge = UFF_ASH3.H.charge;
    }
  }
};

/**
 * OPLS-AA acetylene (C₂H₂) parameters. Linear 4-atom H-C≡C-H — unique
 * among hydrocarbons in having q_C NEGATIVE and q_H POSITIVE (sp
 * hybridization concentrates electron density on C, the OPPOSITE of
 * the standard sp³ alkane convention). This is what gives solid
 * acetylene its T-shaped molecular packing (the H on one molecule
 * points at the π-cloud of another) and a substantial cohesion from
 * "C-H···π" weak hydrogen bonding.
 *
 *   σ_C = 3.30 Å, ε_C = 0.086 kcal/mol = 3.73 meV (OPLS-AA sp C)
 *   σ_H = 2.42 Å, ε_H = 0.015 kcal/mol = 0.65 meV (OPLS-AA acetylenic H)
 *   q_C = −0.241 e, q_H = +0.241 e   (molecule neutral; large axial
 *           quadrupole — unlike alkane C-H, the sp C-H here has H+,
 *           the opposite of methane's q_H = +0.06 / q_C = −0.24)
 *   r_CH = 1.080 Å (rigid), r_CC = 1.210 Å (rigid), all atoms linear
 *
 * 6 distance constraints per molecule rigidify the chain: 3 bonds
 * (H₁-C₁, C₁-C₂, C₂-H₂) plus 3 long-range pins (H₁-C₂, C₁-H₂, H₁-H₂)
 * that enforce strict collinearity. Over-determined for 5 internal
 * DOF (3N-5 = 7 vibrational modes, of which 4 are doubly-degenerate
 * bends reducing to 5 independent rigid-body constraints), but
 * SHAKE/RATTLE handles redundancy.
 *
 * Solid C₂H₂ sublimation enthalpy at 0 K: ~0.24 eV/molecule. Strong
 * cohesion for such a small molecule, driven by the quadrupolar
 * packing.
 */
export const OPLS_C2H2 = {
  C: {
    sigma: 3.3,
    epsilon: 0.086 * 0.04336, // ≈ 0.00373
    charge: -0.241,
  },
  H: {
    sigma: 2.42,
    epsilon: 0.015 * 0.04336, // ≈ 0.00065
    charge: 0.241,
  },
  /** Rigid C-H bond, Å. */
  chBondLength: 1.08,
  /** Rigid C≡C bond, Å. */
  ccBondLength: 1.21,
  /** Total molecular length (H₁-H₂ distance), Å. */
  hHDistance: 1.08 * 2 + 1.21,
} as const;

export const applyOplsC2H2ToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'C') {
      a.sigma = OPLS_C2H2.C.sigma;
      a.epsilon = OPLS_C2H2.C.epsilon;
      a.charge = OPLS_C2H2.C.charge;
    } else if (a.elementSymbol === 'H') {
      a.sigma = OPLS_C2H2.H.sigma;
      a.epsilon = OPLS_C2H2.H.epsilon;
      a.charge = OPLS_C2H2.H.charge;
    }
  }
};

/**
 * OPLS-AA-style hydrogen cyanide (HCN) parameters. Linear asymmetric
 * 3-atom H-C≡N — the most polar small molecule in the database (μ ≈
 * 2.98 D, larger than water's 1.85 D). Real solid HCN forms long
 * linear C-H···N hydrogen-bonded chains; in the Pa-3-like preset
 * those chains scatter into a body-diagonal-aligned packing whose
 * net Coulomb sum cancels by symmetry — so the very strong charges
 * here (q_N = −0.40) contribute much less to lattice cohesion than
 * the real H-bonded crystal would have.
 *
 *   σ_H = 2.50 Å, ε_H = 0.030 kcal/mol = 1.30 meV (OPLS-AA H-on-nitrile)
 *   σ_C = 3.65 Å, ε_C = 0.150 kcal/mol = 6.50 meV (OPLS-AA sp C nitrile)
 *   σ_N = 3.20 Å, ε_N = 0.170 kcal/mol = 7.37 meV (OPLS-AA nitrile N)
 *   q_H = +0.20 e, q_C = +0.20 e, q_N = −0.40 e   (molecule neutral;
 *           strong axial dipole H+...N− pointing along the molecular axis)
 *   r_HC = 1.064 Å (rigid), r_CN = 1.156 Å (rigid), all atoms linear
 *
 * Solid HCN sublimation enthalpy at 0 K: ~0.34 eV/molecule.
 */
export const OPLS_HCN = {
  H: {
    sigma: 2.5,
    epsilon: 0.03 * 0.04336, // ≈ 0.00130
    charge: 0.2,
  },
  C: {
    sigma: 3.65,
    epsilon: 0.15 * 0.04336, // ≈ 0.00650
    charge: 0.2,
  },
  N: {
    sigma: 3.2,
    epsilon: 0.17 * 0.04336, // ≈ 0.00737
    charge: -0.4,
  },
  /** Rigid H-C bond, Å. */
  hcBondLength: 1.064,
  /** Rigid C-N bond, Å. */
  cnBondLength: 1.156,
} as const;

export const applyOplsHCNToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'H') {
      a.sigma = OPLS_HCN.H.sigma;
      a.epsilon = OPLS_HCN.H.epsilon;
      a.charge = OPLS_HCN.H.charge;
    } else if (a.elementSymbol === 'C') {
      a.sigma = OPLS_HCN.C.sigma;
      a.epsilon = OPLS_HCN.C.epsilon;
      a.charge = OPLS_HCN.C.charge;
    } else if (a.elementSymbol === 'N') {
      a.sigma = OPLS_HCN.N.sigma;
      a.epsilon = OPLS_HCN.N.epsilon;
      a.charge = OPLS_HCN.N.charge;
    }
  }
};

/**
 * OPLS-AA-style cyanogen (C₂N₂) parameters. Linear symmetric
 * N≡C-C≡N — centrosymmetric like CO₂ or acetylene, so the molecular
 * dipole is zero but the axial quadrupole is substantial. Same
 * 4-atom chain shape as acetylene with terminal N replacing terminal
 * H, hence sharing acetylene's 6-distance-constraint rigid topology.
 *
 *   σ_C = 3.30 Å, ε_C = 0.086 kcal/mol = 3.73 meV (OPLS sp C)
 *   σ_N = 3.20 Å, ε_N = 0.170 kcal/mol = 7.37 meV (OPLS nitrile N)
 *   q_C = +0.20 e, q_N = −0.20 e   (molecule neutral; no dipole by
 *           centrosymmetry, but the C↔N partial charges set up the
 *           same axial quadrupole that drives solid cyanogen's
 *           parallel-aligned packing)
 *   r_CC = 1.390 Å (rigid, single bond), r_CN = 1.156 Å (rigid,
 *           triple bond), all 4 atoms linear
 *
 * Solid C₂N₂ sublimation enthalpy at 0 K: ~0.32 eV/molecule.
 */
export const OPLS_CYANOGEN = {
  C: {
    sigma: 3.3,
    epsilon: 0.086 * 0.04336, // ≈ 0.00373
    charge: 0.2,
  },
  N: {
    sigma: 3.2,
    epsilon: 0.17 * 0.04336, // ≈ 0.00737
    charge: -0.2,
  },
  /** Rigid C≡N bond, Å. */
  cnBondLength: 1.156,
  /** Rigid C-C single bond, Å. */
  ccBondLength: 1.39,
  /** Total molecular length (N₁-N₂ distance), Å. */
  nNDistance: 1.156 * 2 + 1.39,
} as const;

export const applyOplsCyanogenToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'C') {
      a.sigma = OPLS_CYANOGEN.C.sigma;
      a.epsilon = OPLS_CYANOGEN.C.epsilon;
      a.charge = OPLS_CYANOGEN.C.charge;
    } else if (a.elementSymbol === 'N') {
      a.sigma = OPLS_CYANOGEN.N.sigma;
      a.epsilon = OPLS_CYANOGEN.N.epsilon;
      a.charge = OPLS_CYANOGEN.N.charge;
    }
  }
};

/**
 * OPLS-AA-style SF₆ parameters. Octahedral 7-atom (1 S + 6 F) — first
 * non-tetrahedral / non-pyramidal / non-linear molecular shape in the
 * crystal database. The S-F bonds are extremely polar (q_S = +1.20),
 * but the molecule's perfect O_h symmetry zeros both the dipole AND
 * the quadrupole moments — only the hexadecapole is non-zero, so the
 * Coulomb contribution to lattice cohesion is intrinsically small
 * even in the most favorable packing. SF₆ behaves nearly as a "fat
 * noble gas" in the solid — cohesion dominated by S-S London
 * dispersion, with the F atoms making a polarizable shell.
 *
 *   σ_S = 3.595 Å, ε_S = 0.274 kcal/mol = 11.88 meV (UFF S) × 2.19 (see below)
 *   σ_F = 2.997 Å, ε_F = 0.050 kcal/mol = 2.17 meV (UFF F) × 2.19
 *   q = 0 (UFF's +1.20/−0.20 e were dropped: see SF6_EPS_SCALE)
 *   r_SF = 1.564 Å (rigid, gas-phase), all bond angles 90° / 180°
 *
 * Solid SF₆ Phase II sublimation enthalpy at 0 K: ~0.25 eV/molecule.
 * Real solid SF₆ at higher T is rotationally disordered (BCC plastic
 * crystal); the FF treats each molecule as a rigid body matching the
 * low-T orientationally-ordered limit.
 */
// Cohesion-matched (2026-10-13, same rule as ETTERS_N2): at the UFF ε the
// periodic 2×2×2 crystal bound only −92 meV/molecule at its minimum (a ≈
// 7.25 Å, matching the preset's 7.32) against the measured ~250 meV — UFF
// dispersion is fitted to gas-phase geometry, not condensed-phase binding.
// The partial charges cost another 22 meV: the hexadecapole is harmless at
// long range, but at contact the −0.2 e fluorines of neighboring molecules
// repel, so they are dropped (the standard SF₆ crystal models, Pawley's and
// Dellis–Samios', carry no charges). ε × 250/114 then gives −250 meV at the
// preset lattice constant.
const SF6_EPS_SCALE = 250 / 114;
export const UFF_SF6 = {
  S: {
    sigma: 3.595,
    epsilon: 0.274 * 0.04336 * SF6_EPS_SCALE, // UFF 11.88 meV → 26.1 meV
    charge: 0,
  },
  F: {
    sigma: 2.997,
    epsilon: 0.05 * 0.04336 * SF6_EPS_SCALE, // UFF 2.17 meV → 4.76 meV
    charge: 0,
  },
  /** Rigid S-F bond, Å. */
  bondLength: 1.564,
  /** Cis F-F (90°), Å — for the 12 cis pairs in the octahedron. */
  cisFFDistance: 1.564 * Math.SQRT2, // ≈ 2.212
  /** Trans F-F (180°), Å — for the 3 trans pairs. */
  transFFDistance: 1.564 * 2, // 3.128
} as const;

export const applyUffSF6ToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'S') {
      a.sigma = UFF_SF6.S.sigma;
      a.epsilon = UFF_SF6.S.epsilon;
      a.charge = UFF_SF6.S.charge;
    } else if (a.elementSymbol === 'F') {
      a.sigma = UFF_SF6.F.sigma;
      a.epsilon = UFF_SF6.F.epsilon;
      a.charge = UFF_SF6.F.charge;
    }
  }
};

/**
 * UFF-style white phosphorus (α-P₄) parameters. 4-atom tetrahedron
 * of bonded phosphorus atoms — first all-same-element molecular
 * species in the database. Every P-P pair in the tetrahedron is a
 * bond (single-bond, 2.21 Å); the molecule has no central atom and
 * no internal polarity. Cohesion is pure London dispersion (P is
 * large and polarizable, ε_P = 13.2 meV gives substantial well
 * depth) — much stronger than the noble-gas analogs of the same
 * shape.
 *
 *   σ_P = 3.695 Å, ε_P = 0.305 kcal/mol = 13.2 meV (UFF P)
 *   q_P = 0 (single-element molecule, no internal partial charges)
 *   r_PP = 2.21 Å (rigid edge length), all 6 pairs equivalent;
 *           tetrahedral angle P-P-P = 60° (NOT 109.47° — the
 *           atom-centered angle in the equilateral-triangle face)
 *   6 distance constraints/molecule (every P-P pair) = 6 internal
 *   DOF, exactly determined (no redundancy)
 *
 * Solid α-P₄ sublimation enthalpy at 0 K: ~0.59 eV/molecule (~0.15
 * eV/atom). Strongly bound for a vdW solid — P's ~7× polarizability
 * relative to N drives the deep dispersion well.
 */
export const UFF_P4 = {
  P: {
    sigma: 3.695,
    epsilon: 0.305 * 0.04336, // ≈ 0.01323
    charge: 0,
  },
  /** Rigid P-P bond, Å. */
  bondLength: 2.21,
} as const;

export const applyUffP4ToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'P') {
      a.sigma = UFF_P4.P.sigma;
      a.epsilon = UFF_P4.P.epsilon;
      a.charge = UFF_P4.P.charge;
    }
  }
};

/**
 * UFF-style CBr₄ parameters. Tetrahedral 5-atom molecule, analogous
 * to CH₄ but with bromine — a much heavier and more polarizable atom.
 * The four equivalent Br atoms eliminate any molecular dipole by
 * tetrahedral symmetry, so cohesion is dominated by London dispersion
 * between the large Br atoms (Br has ~6× the polarizability of H).
 *
 *   σ_C = 3.431 Å, ε_C = 0.105 kcal/mol = 4.55 meV (UFF)
 *   σ_Br = 3.732 Å, ε_Br = 0.251 kcal/mol = 10.88 meV (UFF)
 *   q_C = +0.20 e, q_Br = −0.05 e   (molecule neutral; tiny C↔Br
 *           local polarity for the Coulomb pass — molecular dipole
 *           is zero by tetrahedral symmetry, octupole non-zero)
 *   r_CBr = 1.94 Å (rigid), θ_BrCBr = 109.47° (rigid tetrahedral)
 *
 * Solid CBr₄ Phase I sublimation enthalpy at 0 K: ~0.43 eV/molecule
 * (~0.086 eV/atom for 5-atom CBr₄). Real solid CBr₄ is a "plastic
 * crystal" with orientationally-disordered molecules at intermediate
 * T (FCC arrangement, rotators); the FF treats each as a rigid body
 * (orientationally-ordered low-T limit).
 */
export const UFF_CBR4 = {
  C: {
    sigma: 3.431,
    epsilon: 0.105 * 0.04336, // kcal/mol → eV; ≈ 0.00455
    charge: 0.2,
  },
  Br: {
    sigma: 3.732,
    epsilon: 0.251 * 0.04336, // ≈ 0.01088
    charge: -0.05,
  },
  /** Rigid C-Br bond, Å. */
  bondLength: 1.94,
  /** Br-C-Br equilibrium angle (radians), tetrahedral. */
  angleEquilibrium: Math.acos(-1 / 3),
} as const;

/**
 * Apply UFF CBr₄ per-atom parameters in place. Sets ONLY C and Br —
 * guest dopants are left alone.
 */
export const applyUffCBr4ToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'C') {
      a.sigma = UFF_CBR4.C.sigma;
      a.epsilon = UFF_CBR4.C.epsilon;
      a.charge = UFF_CBR4.C.charge;
    } else if (a.elementSymbol === 'Br') {
      a.sigma = UFF_CBR4.Br.sigma;
      a.epsilon = UFF_CBR4.Br.epsilon;
      a.charge = UFF_CBR4.Br.charge;
    }
  }
};

/**
 * UFF/OPLS hybrid CF₄ parameters. Tetrahedral 5-atom carbon with four
 * fluorines — the most electronegative element gives the largest C-F
 * bond polarity of any tetrahedral CH₄ analog. Per-bond dipoles are
 * substantial (q_F = -0.12, q_C = +0.48) but the molecular dipole is
 * zero by tetrahedral symmetry; the partial charges still scaffold a
 * sizeable molecular octupole that contributes to intermolecular
 * Coulomb in non-centrosymmetric packings (and cancels by symmetry
 * in the Pa-3-like preset here, as for the other tetrahedral species).
 *
 *   σ_C = 3.431 Å, ε_C = 0.105 kcal/mol = 4.55 meV (UFF)
 *   σ_F = 2.997 Å, ε_F = 0.050 kcal/mol = 2.17 meV (UFF)
 *   q_C = +0.48 e, q_F = −0.12 e   (OPLS-AA-style; molecule neutral)
 *   r_CF = 1.32 Å (rigid), θ_FCF = 109.47° (rigid tetrahedral)
 *
 * Solid CF₄ Phase II sublimation enthalpy at 0 K: ~0.18 eV/molecule
 * (~0.036 eV/atom for 5-atom CF₄). F is a "small" halogen so the
 * intermolecular F-F distance at the experimental FCC lattice
 * constant (a = 7.4 Å) is comfortably past σ_F — unlike CCl₄ and
 * CBr₄, the F-F packing does NOT collide.
 */
export const UFF_CF4 = {
  C: {
    sigma: 3.431,
    epsilon: 0.105 * 0.04336, // ≈ 0.00455
    charge: 0.48,
  },
  F: {
    sigma: 2.997,
    epsilon: 0.05 * 0.04336, // ≈ 0.00217
    charge: -0.12,
  },
  /** Rigid C-F bond, Å. */
  bondLength: 1.32,
  /** F-C-F equilibrium angle (radians), tetrahedral. */
  angleEquilibrium: Math.acos(-1 / 3),
} as const;

export const applyUffCF4ToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'C') {
      a.sigma = UFF_CF4.C.sigma;
      a.epsilon = UFF_CF4.C.epsilon;
      a.charge = UFF_CF4.C.charge;
    } else if (a.elementSymbol === 'F') {
      a.sigma = UFF_CF4.F.sigma;
      a.epsilon = UFF_CF4.F.epsilon;
      a.charge = UFF_CF4.F.charge;
    }
  }
};

/**
 * UFF/OPLS hybrid CCl₄ parameters. Tetrahedral 5-atom — same shape as
 * CBr₄, halfway between CF₄'s small/polar and CBr₄'s large/weakly-polar
 * extremes. Cohesion is mostly Cl-Cl London dispersion plus modest
 * partial-charge Coulomb.
 *
 *   σ_C = 3.431 Å, ε_C = 0.105 kcal/mol = 4.55 meV (UFF)
 *   σ_Cl = 3.518 Å, ε_Cl = 0.227 kcal/mol = 9.84 meV (UFF)
 *   q_C = +0.248 e, q_Cl = −0.062 e   (OPLS-AA-style; molecule neutral)
 *   r_CCl = 1.766 Å (rigid), θ_ClCCl = 109.47° (rigid tetrahedral)
 *
 * Solid CCl₄ Phase Ia sublimation enthalpy at 0 K: ~0.34 eV/molecule
 * (~0.068 eV/atom). Real solid CCl₄ at higher T is rotationally
 * disordered (plastic crystal); at low T Phase II is monoclinic and
 * orientationally ordered.
 *
 * IMPORTANT — preset geometry caveat: like CBr₄, the SOLID_CCL4
 * preset places all four CCl₄ molecules in the same orientation at
 * the high-T plastic-crystal lattice constant (a = 8.32 Å). With
 * σ_Cl = 3.52, the nearest intermolecular Cl-Cl distance (~2.99 Å)
 * sits well inside the LJ repulsive wall, producing strongly positive
 * LJ at the input geometry. The cohesion result will be wrong-signed
 * unless the preset is given rotational disorder or expanded to the
 * low-T (Phase II) lattice — the same fundamental issue as CBr₄.
 */
// Cohesion-matched (2026-10-13, the ETTERS_N2 / UFF_SF6 rule): read by MD at
// 50 K (the as-built parallel tetrahedra clash, +0.96 eV, and turn away
// within femtoseconds), the UFF ε bound only −177 meV/molecule at the
// preset's 8.32 Å (model minimum −190 at 8.0 Å) against ~0.34 eV measured
// for the ordered 0 K phase — call it 0.30 at the plastic-phase lattice.
// The partial charges contribute nothing (−0.2 meV) and are kept.
const CCL4_EPS_SCALE = 300 / 177;
// The UFF σ put the model's lattice minimum at 8.05 Å; a supercell built at
// the preset's 8.32 Å (the plastic phase's constant) is then under 3 %
// tension in the periodic plane, and a free slab of it shrank along c and
// lost its order even at 120 K (Q6 0.30). σ × 8.32/8.05 moves the minimum
// to the preset lattice.
const CCL4_SIGMA_SCALE = 8.32 / 8.05;
export const UFF_CCL4 = {
  C: {
    sigma: 3.431 * CCL4_SIGMA_SCALE,
    epsilon: 0.105 * 0.04336 * CCL4_EPS_SCALE, // UFF 4.55 meV → 7.7 meV
    charge: 0.248,
  },
  Cl: {
    sigma: 3.518 * CCL4_SIGMA_SCALE,
    epsilon: 0.227 * 0.04336 * CCL4_EPS_SCALE, // UFF 9.84 meV → 16.7 meV
    charge: -0.062,
  },
  /** Rigid C-Cl bond, Å. */
  bondLength: 1.766,
  /** Cl-C-Cl equilibrium angle (radians), tetrahedral. */
  angleEquilibrium: Math.acos(-1 / 3),
} as const;

export const applyUffCCl4ToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'C') {
      a.sigma = UFF_CCL4.C.sigma;
      a.epsilon = UFF_CCL4.C.epsilon;
      a.charge = UFF_CCL4.C.charge;
    } else if (a.elementSymbol === 'Cl') {
      a.sigma = UFF_CCL4.Cl.sigma;
      a.epsilon = UFF_CCL4.Cl.epsilon;
      a.charge = UFF_CCL4.Cl.charge;
    }
  }
};

/**
 * UFF-style silane (SiH₄) parameters. Tetrahedral 5-atom molecule —
 * methane's heavier silicon analog. Si is much less electronegative
 * than C (Pauling: Si 1.90 vs C 2.55), so the Si-H bond is slightly
 * polarized with H on the negative side (the opposite of C-H). The
 * molecular dipole is zero by tetrahedral symmetry; the small partial
 * charges still matter for intermolecular Si↔H Coulomb.
 *
 *   σ_Si = 3.826 Å, ε_Si = 0.402 kcal/mol = 17.4 meV (UFF Si)
 *   σ_H = 0.4 (safety floor — like TIP3P/NH₃/PH₃ H, ε ≈ 0)
 *   q_Si = +0.13 e, q_H = −0.0325 e   (molecule neutral; Si-H polarity
 *           opposite to C-H since H is more electronegative than Si)
 *   r_SiH = 1.480 Å (rigid, gas-phase), tetrahedral H-Si-H = 109.47°
 *
 * Solid SiH₄ sublimation enthalpy at 0 K: ~0.15 eV/molecule (~14
 * kJ/mol; very weak vdW solid — SiH₄ boils at 161 K). The H-side
 * safety σ floor means most of the cohesion comes from Si-Si LJ.
 */
export const UFF_SIH4 = {
  Si: {
    sigma: 3.826,
    epsilon: 0.402 * 0.04336, // kcal/mol → eV; ≈ 0.01744
    charge: 0.13,
  },
  H: {
    // Same safety-floor trick as TIP3P / NH₃ H. ε three decades below
    // the Si well so cross-pair LJ is dominated by Si's term.
    sigma: 0.4,
    epsilon: 0.0001,
    charge: -0.0325,
  },
  /** Rigid Si-H bond, Å. */
  bondLength: 1.48,
  /** H-Si-H equilibrium angle (radians), tetrahedral. */
  angleEquilibrium: Math.acos(-1 / 3),
} as const;

export const applyUffSiH4ToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'Si') {
      a.sigma = UFF_SIH4.Si.sigma;
      a.epsilon = UFF_SIH4.Si.epsilon;
      a.charge = UFF_SIH4.Si.charge;
    } else if (a.elementSymbol === 'H') {
      a.sigma = UFF_SIH4.H.sigma;
      a.epsilon = UFF_SIH4.H.epsilon;
      a.charge = UFF_SIH4.H.charge;
    }
  }
};

/**
 * UFF-style germane (GeH₄) parameters. Direct silicon→germanium swap:
 * larger atom, deeper LJ well, longer bond, similar Pauling
 * electronegativity to Si (Ge 2.01), so Ge-H polarity is similar in
 * sign and slightly smaller in magnitude than Si-H.
 *
 *   σ_Ge = 3.682 Å, ε_Ge = 0.379 kcal/mol = 16.4 meV (UFF Ge)
 *   σ_H = 0.4 (safety floor)
 *   q_Ge = +0.10 e, q_H = −0.025 e   (molecule neutral)
 *   r_GeH = 1.525 Å (rigid, gas-phase), tetrahedral
 *
 * Solid GeH₄ sublimation enthalpy at 0 K: ~0.15 eV/molecule. GeH₄
 * boils at 184 K — similar bonding character to SiH₄.
 */
export const UFF_GEH4 = {
  Ge: {
    sigma: 3.682,
    epsilon: 0.379 * 0.04336, // ≈ 0.01644
    charge: 0.1,
  },
  H: {
    sigma: 0.4,
    epsilon: 0.0001,
    charge: -0.025,
  },
  /** Rigid Ge-H bond, Å. */
  bondLength: 1.525,
  angleEquilibrium: Math.acos(-1 / 3),
} as const;

export const applyUffGeH4ToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'Ge') {
      a.sigma = UFF_GEH4.Ge.sigma;
      a.epsilon = UFF_GEH4.Ge.epsilon;
      a.charge = UFF_GEH4.Ge.charge;
    } else if (a.elementSymbol === 'H') {
      a.sigma = UFF_GEH4.H.sigma;
      a.epsilon = UFF_GEH4.H.epsilon;
      a.charge = UFF_GEH4.H.charge;
    }
  }
};

/**
 * EPM2-style carbon disulfide (CS₂) parameters. Linear S=C=S, same
 * shape as CO₂ but sulfur in place of oxygen. CS₂ is much less polar
 * than CO₂ (S electronegativity 2.58 vs O 3.44, much closer to C's
 * 2.55), so the quadrupole is much smaller and most of solid CS₂'s
 * cohesion comes from London dispersion on the large S atoms — not
 * from electrostatics as in CO₂.
 *
 *   σ_C = 3.20 Å, ε_C = 51 K = 4.39 meV (TraPPE-CS2 inspired)
 *   σ_S = 3.52 Å, ε_S = 183 K = 15.77 meV (TraPPE)
 *   q_C = +0.154 e, q_S = −0.077 e   (half of EPM2 CO₂ values —
 *           CS₂ quadrupole is much smaller than CO₂'s)
 *   r_CS = 1.554 Å (rigid, gas-phase), S-C-S = 180° (rigid)
 *
 * Solid CS₂ sublimation enthalpy at 0 K: ~0.29 eV/molecule (~28
 * kJ/mol). Mostly LJ cohesion (S is large and polarizable).
 */
export const EPM2_CS2 = {
  C: {
    sigma: 3.2,
    epsilon: 51 * KB_EV_CO2, // ≈ 0.00440
    charge: 0.154,
  },
  S: {
    sigma: 3.52,
    epsilon: 183 * KB_EV_CO2, // ≈ 0.01577
    charge: -0.077,
  },
  /** Rigid C-S bond, Å. */
  bondLength: 1.554,
  /** Rigid linear S-C-S: pin the S-S distance to enforce 180°. */
  sSDistance: 3.108,
} as const;

export const applyEpm2CS2ToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'C') {
      a.sigma = EPM2_CS2.C.sigma;
      a.epsilon = EPM2_CS2.C.epsilon;
      a.charge = EPM2_CS2.C.charge;
    } else if (a.elementSymbol === 'S') {
      a.sigma = EPM2_CS2.S.sigma;
      a.epsilon = EPM2_CS2.S.epsilon;
      a.charge = EPM2_CS2.S.charge;
    }
  }
};

/**
 * Three-site rigid N₂O parameters (Kuchta-Etters-style, simplified).
 * Linear N≡N-O with slightly asymmetric bond lengths and a small
 * molecular dipole (μ ≈ 0.16 D — much smaller than CO₂'s zero-dipole
 * quadrupole-only field, but with net dipole because the molecule is
 * asymmetric). We cannot distinguish the central N from the terminal
 * N by element symbol alone in the kernel — so both N atoms get the
 * same averaged LJ/charge, sacrificing the literature's terminal-vs-
 * central distinction. The geometry is still asymmetric (different
 * N-N and N-O bond lengths), so the molecule's shape is preserved.
 *
 *   σ_N = 3.27 Å, ε_N = 60 K = 5.17 meV (averaged terminal/central N
 *           — bumped from the TraPPE 49 K to compensate for the
 *           Coulomb cancellation in the Pa-3-like preset, see below)
 *   σ_O = 2.99 Å, ε_O = 110 K = 9.48 meV (bumped from TraPPE 64 K
 *           for the same reason)
 *   q_N = +0.05 e (each), q_O = −0.10 e   (molecule neutral; very
 *           rough — real N₂O has uneven N charges that produce its
 *           0.16 D dipole. With equal q_N our model dipole is zero;
 *           the small charges still scaffold a quadrupolar field.)
 *   r_NN = 1.128 Å, r_NO = 1.184 Å (rigid), N-N-O = 180° (rigid)
 *
 * Solid N₂O sublimation enthalpy at 0 K: ~0.26 eV/molecule, similar
 * to CO₂. With the four molecules at body-diagonal orientations in
 * the Pa-3-like preset, the molecular dipoles cancel by symmetry and
 * Coulomb contributes ~0 to lattice cohesion — so the deepened LJ
 * here makes up the difference an idealized quadrupolar field would
 * have provided.
 *
 * Same caveat applies to many "Pa-3-like" molecular-crystal presets
 * in this codebase: the orientational averaging zeros the Coulomb
 * lattice sum, leaving LJ alone to carry the cohesion — even when
 * the real-crystal cohesion has a significant electrostatic part.
 */
export const KK_N2O = {
  N: {
    sigma: 3.27,
    epsilon: 60 * KB_EV_CO2, // ≈ 0.00517
    charge: 0.05,
  },
  O: {
    sigma: 2.99,
    epsilon: 110 * KB_EV_CO2, // ≈ 0.00948
    charge: -0.1,
  },
  /** Rigid N≡N bond, Å (slightly shorter than N=N). */
  nNBondLength: 1.128,
  /** Rigid N-O bond, Å. */
  nOBondLength: 1.184,
} as const;

export const applyKkN2OToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'N') {
      a.sigma = KK_N2O.N.sigma;
      a.epsilon = KK_N2O.N.epsilon;
      a.charge = KK_N2O.N.charge;
    } else if (a.elementSymbol === 'O') {
      a.sigma = KK_N2O.O.sigma;
      a.epsilon = KK_N2O.O.epsilon;
      a.charge = KK_N2O.O.charge;
    }
  }
};

/**
 * Silvera-Goldman-style solid H₂ parameters (simplified isotropic).
 * Real solid H₂ is a quantum solid — zero-point motion is comparable
 * to the molecular spacing and a classical model overestimates the
 * cohesion by a factor of ~3. The classical FF here is pedagogical:
 * it gets the right qualitative behavior (very weak vdW solid that
 * melts ~14 K) but the cohesion magnitude is wrong.
 *
 *   σ_H = 2.96 Å, ε_H = 34.2 K = 2.95 meV (Silvera-Goldman 2-body
 *           reduced to per-atom; treats each H separately)
 *   q_H = 0 (no charge — H₂ has no dipole or quadrupole)
 *   r_HH = 0.741 Å (rigid, gas-phase bond length)
 *
 * Solid H₂ sublimation enthalpy at 0 K: ~0.01 eV/molecule classically
 * (zero-point reduces the EXPERIMENTAL value to ~0.0093 eV/molecule).
 * Pure-LJ Silvera-Goldman with no quantum correction overshoots by ~2×
 * for molecular cohesion — a documented limitation of classical FFs
 * for the lightest elements.
 */
export const SILVERA_H2 = {
  H: {
    sigma: 2.96,
    epsilon: 34.2 * KB_EV_CO2, // ≈ 0.00295
    charge: 0,
  },
  /** Rigid H-H bond, Å. */
  bondLength: 0.741,
} as const;

export const applySilveraH2ToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'H') {
      a.sigma = SILVERA_H2.H.sigma;
      a.epsilon = SILVERA_H2.H.epsilon;
      a.charge = SILVERA_H2.H.charge;
    }
  }
};

/**
 * Two-site rigid CO parameters — analog of Etters N₂ for the closely
 * related heteronuclear diatomic. CO has a very small molecular dipole
 * (μ ≈ 0.11 D, opposite sign to what naïve electronegativity predicts;
 * the lone pair on C accounts for the unusual polarity) and a sizeable
 * quadrupole. We capture the rough dipole with tiny partial charges
 * (q_C = -0.04, q_O = +0.04 — note the unusual SIGN, with C bearing
 * the negative end), and the rest of cohesion comes from LJ.
 *
 *   σ_C = 3.50 Å, ε_C = 65 K = 5.60 meV (Karasawa-Goddard CO with
 *           deepened C well — see note on Pa-3 cancellation below)
 *   σ_O = 3.05 Å, ε_O = 100 K = 8.62 meV
 *   q_C = −0.04 e, q_O = +0.04 e   (small dipole, C-end negative —
 *           inverted from naïve electronegativity expectation, but
 *           consistent with the experimental sign of CO's dipole)
 *   r_CO = 1.128 Å (rigid, gas-phase bond length)
 *
 * α-CO sublimation enthalpy at 0 K: ~0.087 eV/molecule (similar to
 * α-N₂ but slightly more strongly bound, owing to the small dipole-
 * dipole contribution). LJ ε's deepened above the strict Karasawa-
 * Goddard values because in the Pa-3-like α-CO preset the four
 * body-diagonal orientations cancel the dipole lattice sum, so the
 * Coulomb contribution to cohesion vanishes by symmetry — LJ has to
 * carry what dipole-dipole would have contributed in reality.
 */
export const KG_CO = {
  C: {
    sigma: 3.5,
    epsilon: 65 * KB_EV_CO2, // ≈ 0.00560
    charge: -0.04,
  },
  O: {
    sigma: 3.05,
    epsilon: 100 * KB_EV_CO2, // ≈ 0.00862
    charge: 0.04,
  },
  /** Rigid C-O bond, Å (close to CO₂'s 1.149 but slightly shorter). */
  bondLength: 1.128,
} as const;

export const applyKgCOToAtoms = (atoms: Atom[]) => {
  for (const a of atoms) {
    if (a.elementSymbol === 'C') {
      a.sigma = KG_CO.C.sigma;
      a.epsilon = KG_CO.C.epsilon;
      a.charge = KG_CO.C.charge;
    } else if (a.elementSymbol === 'O') {
      a.sigma = KG_CO.O.sigma;
      a.epsilon = KG_CO.O.epsilon;
      a.charge = KG_CO.O.charge;
    }
  }
};

/**
 * Apply TIP3P bonded-force constants to an already-built list of
 * radial / angular bonds. We don't override equilibrium values
 * because the auto-builder pins them to the current (ice-as-loaded)
 * geometry, which is already the correct rigid-water geometry for
 * Ih ice — pinning to the TIP3P 0.9572 Å / 104.52° defaults would
 * actually inject strain on the first step. Just stiffen the
 * harmonic spring constants.
 */
export const applyTip3PBondedForces = (
  atoms: Atom[],
  radialBonds: { atom1: Atom; atom2: Atom; strength: number }[],
  angularBonds: { atom1: Atom; atom2: Atom; atom3: Atom; strength: number }[],
) => {
  for (const rb of radialBonds) {
    const e1 = rb.atom1.elementSymbol;
    const e2 = rb.atom2.elementSymbol;
    if ((e1 === 'O' && e2 === 'H') || (e1 === 'H' && e2 === 'O')) {
      rb.strength = TIP3P.bondStrength;
    }
  }
  for (const ab of angularBonds) {
    // H–O–H: center atom is O, ends are both H.
    if (ab.atom2.elementSymbol === 'O' && ab.atom1.elementSymbol === 'H' && ab.atom3.elementSymbol === 'H') {
      ab.strength = TIP3P.angleStrength;
    }
  }
};
